import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../utils/LanguageContext';
import axios from 'axios';
import { Loader, ChevronDown } from 'lucide-react';
import getHelmetConfig from "../utils/helmetConfig";
import {Helmet} from "react-helmet-async";

const API_BASE_URL = 'https://texla.uz/api/v1';

// Skeleton Loader
const FAQSkeleton = () => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 mb-4 w-3/4"></div>
        <div className="h-4 bg-gray-200 mb-2"></div>
        <div className="h-4 bg-gray-200 w-5/6"></div>
    </div>
);

const FAQPage = () => {
    const { t, language } = useLanguage();
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        fetchFAQs();
    }, [language]);

    const fetchFAQs = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/faq/`);
            if (response.data && Array.isArray(response.data)) {
                const formattedFAQs = response.data.map(item => ({
                    id: item.id,
                    question: item[`question_${language}`] || item.question_uz || item.question,
                    answer: item[`answer_${language}`] || item.answer_uz || item.answer,
                }));
                setFaqs(formattedFAQs);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const toggleFAQ = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader className="animate-spin text-gray-400" size={48} />
            </div>
        );
    }

    const helmetConfig = getHelmetConfig(language);
    const pageConfig = helmetConfig.faqs;


    return (
        <div className="bg-white">
            <Helmet>
                <title>{pageConfig.title}</title>
                <meta name="description" content={pageConfig.description} />
                <meta name="keywords" content={pageConfig.keywords} />
            </Helmet>
            {/* Breadcrumb */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
                    <Link to={"/"} className="hover:text-gray-900">{t('breadcrumb.home')}</Link>
                    <span>/</span>
                    <span className="text-gray-900">{t('header.faqs')}</span>
                </div>
            </div>

            {/* Header */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('header.faqs')}</h1>
                <p className="text-gray-600 text-lg">
                    {t('faqs.description')}
                </p>
            </section>

            {/* FAQ List */}
            {faqs.length > 0 ? (
                <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="space-y-4">
                        {faqs.map((faq) => (
                            <div
                                key={faq.id}
                                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition"
                            >
                                <button
                                    onClick={() => toggleFAQ(faq.id)}
                                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition"
                                >
                                    <h3 className="text-lg font-semibold text-gray-900 text-left">
                                        {faq.question}
                                    </h3>
                                    <ChevronDown
                                        size={24}
                                        className={`text-gray-600 transition-transform flex-shrink-0 ml-4 ${
                                            expandedId === faq.id ? 'transform rotate-180' : ''
                                        }`}
                                    />
                                </button>

                                {expandedId === faq.id && (
                                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                                        <div className="text-gray-600 prose prose-sm max-w-none">
                                            <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            ) : (
                <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
                    <p className="text-gray-600 text-lg">{t('shop.noProductsFound')}</p>
                </section>
            )}
        </div>
    );
};

export default FAQPage;